import { z } from "zod";

// ── Route 21: GET /api/integrations/gsc/connections ──────────────

/**
 * Single GSC connection row returned inside GscConnectionsResponse.
 */
export const GscConnection = z.object({
  id: z.string(),
  site_url: z.string(),
  google_account_email: z.string(),
  last_synced_at: z.string().nullable(),
  last_sync_error: z.string().nullable(),
  created_at: z.string(),
});
export type GscConnection = z.infer<typeof GscConnection>;

/**
 * Wire-level response for GET /api/integrations/gsc/connections.
 * Returns the project's GSC connection or null if none exists.
 */
export const GscConnectionsResponse = z.object({
  connection: GscConnection.nullable(),
});
export type GscConnectionsResponse = z.infer<typeof GscConnectionsResponse>;

// ── Route 22: POST /api/integrations/gsc/sync ────────────────────

/**
 * Wire-level response for POST /api/integrations/gsc/sync.
 * Returns the Temporal workflow handle for the triggered sync job.
 */
export const GscSyncResponse = z.object({
  workflow_id: z.string(),
  run_id: z.string(),
});
export type GscSyncResponse = z.infer<typeof GscSyncResponse>;

// ── Route 23: GET /api/integrations/gsc/sync/status ──────────────

/**
 * Result payload when a GSC sync workflow completes successfully.
 */
export const GscSyncResult = z.object({
  importId: z.string(),
  rowsFetched: z.number(),
  rowsUpserted: z.number(),
});
export type GscSyncResult = z.infer<typeof GscSyncResult>;

/**
 * Wire-level response for GET /api/integrations/gsc/sync/status.
 * Maps Temporal workflow state to a 3-state model.
 */
export const GscSyncStatusResponse = z.object({
  status: z.enum(["Running", "Completed", "Failed"]),
  result: GscSyncResult.optional(),
  error: z.string().optional(),
});
export type GscSyncStatusResponse = z.infer<typeof GscSyncStatusResponse>;

// ── Route 24: POST /api/integrations/shopify ─────────────────────

/**
 * Store row shape returned from Shopify integration responses.
 */
export const ShopifyStore = z.object({
  id: z.string(),
  shop_domain: z.string(),
  primary_domain: z.string().nullable(),
  scopes: z.array(z.string()),
  created_at: z.string().optional(),
});
export type ShopifyStore = z.infer<typeof ShopifyStore>;

/**
 * Wire-level response for POST /api/integrations/shopify.
 * Returns the newly-connected store record plus shop metadata
 * fetched from the Shopify Admin API during validation.
 */
export const ShopifyConnectResponse = z.object({
  store: ShopifyStore,
  shop: z
    .object({
      name: z.string().optional(),
      primaryDomain: z
        .object({ host: z.string(), url: z.string() })
        .nullable()
        .optional(),
    })
    .optional(),
});
export type ShopifyConnectResponse = z.infer<typeof ShopifyConnectResponse>;

// ── Route 25: GET /api/integrations/shopify ──────────────────────

/**
 * Wire-level response for GET /api/integrations/shopify.
 * Returns all connected Shopify stores, optionally filtered by
 * project_id.
 */
export const ShopifyListResponse = z.object({
  stores: z.array(ShopifyStore),
});
export type ShopifyListResponse = z.infer<typeof ShopifyListResponse>;

// ── Route 26: DELETE /api/integrations/shopify/:storeId ──────────

/**
 * Wire-level response for DELETE /api/integrations/shopify/:storeId.
 * Returns a simple acknowledgement that the store was disconnected.
 */
export const ShopifyDeleteResponse = z.object({
  ok: z.literal(true),
});
export type ShopifyDeleteResponse = z.infer<typeof ShopifyDeleteResponse>;

// ── Route 27: POST /api/integrations/shopify/discover ────────────

/**
 * Single discoverable resource returned from a connected Shopify
 * store.
 */
export const ShopifyDiscoverResource = z.object({
  type: z.string(),
  handle: z.string(),
  title: z.string(),
  blogHandle: z.string().optional(),
});
export type ShopifyDiscoverResource = z.infer<typeof ShopifyDiscoverResource>;

/**
 * Wire-level response for POST /api/integrations/shopify/discover.
 * Returns all importable resources found in the store.
 */
export const ShopifyDiscoverResponse = z.object({
  resources: z.array(ShopifyDiscoverResource),
});
export type ShopifyDiscoverResponse = z.infer<typeof ShopifyDiscoverResponse>;

// ── Route 28: POST /api/integrations/shopify/import ──────────────

/**
 * Wire-level response for POST /api/integrations/shopify/import.
 * Returns the Temporal workflow handle for the triggered import job.
 */
export const ShopifyImportResponse = z.object({
  workflow_id: z.string(),
  run_id: z.string(),
  status: z.string(),
});
export type ShopifyImportResponse = z.infer<typeof ShopifyImportResponse>;
